import { ArticleNumberInput } from '@/components/ui/article-number-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiLanguageInput } from '@/components/ui/multi-language-input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useInsurance, useUpdateInsurance } from '@/hooks/use-insurances';
import { createInsuranceSchema, type InsuranceFormData } from '@fame/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function EditInsurancePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: insurance, isLoading } = useInsurance(id!);
  const updateInsurance = useUpdateInsurance();

  const form = useForm<InsuranceFormData>({
    resolver: zodResolver(createInsuranceSchema) as Resolver<InsuranceFormData>,
    defaultValues: {
      projectId: '',
      internalName: '',
      description: { nl: '', fr: '', en: '' },
      code: 0,
      articleNumber: '',
      price: 0,
      isActive: true,
    },
  });

  useEffect(() => {
    if (insurance) {
      form.reset({
        projectId: insurance.projectId,
        internalName: insurance.internalName,
        description: insurance.description,
        code: Number(insurance.code) || 0,
        articleNumber: insurance.articleNumber,
        price: Number(insurance.price) || 0,
        isActive: insurance.isActive,
      });
    }
  }, [insurance, form]);

  const onSubmit = async (data: InsuranceFormData) => {
    await updateInsurance.mutateAsync({ id: id!, data });
    navigate(`/projecten/${insurance?.projectId}`);
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!insurance) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Verzekering niet gevonden</p>
        <Button asChild variant="link">
          <Link to="/projecten">Terug naar projecten</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/projecten/${insurance.projectId}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Verzekering Bewerken</h1>
          <p className="text-muted-foreground">{insurance.internalName}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Verzekering Informatie</CardTitle>
          <CardDescription>Bewerk de gegevens van deze verzekeringsoptie</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="internalName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interne Naam</FormLabel>
                    <FormControl>
                      <Input placeholder="bijv. Annulatieverzekering" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beschrijving (voor factuur)</FormLabel>
                    <FormControl>
                      <MultiLanguageInput placeholder="bijv. Annulatieverzekering" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" step="1" placeholder="bijv. 201" {...field} />
                      </FormControl>
                      <FormDescription>Unieke code voor dit project</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="articleNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Artikelnummer</FormLabel>
                      <FormControl>
                        <ArticleNumberInput value={field.value} onChange={field.onChange} placeholder="bijv. VATE21%" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prijs</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" placeholder="0,00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Actief</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to={`/projecten/${insurance.projectId}`}>Annuleren</Link>
                </Button>
                <Button type="submit" disabled={updateInsurance.isPending}>
                  {updateInsurance.isPending ? 'Bezig met opslaan...' : 'Opslaan'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
